import { useCallback, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import cx from 'classnames'
import { useRouter } from 'next/router'
import { useSearchParams } from 'next/navigation'
import { Heading } from '~elements'
import BackGroundImage from './elements/BackGroundImage'
import Loading from './elements/Loading'
import { GridView } from './views'
import ViewButtonGroup from './elements/ViewButtonGroup'
import EventDetails from 'components/elements/EventDetails/EventDetails'
import { ArtistProps } from './types'
import styles from './Calendar.module.scss'
import { useCalendar } from './hooks/useCalendar'
import { useWindowSize } from 'utils/hooks'
import { collectArtistIds } from './utils'
import { DATE_FORMAT, SLUG_SHOWS } from '~constants'
import { disableBodyScroll } from 'utils'
import { EventFormatter } from './utils/EventFormatter'
import { EventsByDate, FormattedEvent } from './types/Event'
import { CalendarView } from './views/CalendarView/CalendarView'
import { AssetType, PageData, RichTextType } from '~types'
import { GridViewProps } from 'types/calendar'

export interface CalendarProps {
  heading: string
  backgroundImage?: AssetType
  showsGridView: GridViewProps
  eventDetailsDisclaimer: RichTextType
  artists: Array<ArtistProps>
  groupTicketsOfferText?: RichTextType
  premiumSuitePageLink?: PageData
}

const Calendar = ({
  heading,
  backgroundImage,
  showsGridView,
  eventDetailsDisclaimer,
  artists,
  groupTicketsOfferText,
  premiumSuitePageLink
}: CalendarProps) => {
  const { isBreakpoint: isBelowMd } = useWindowSize('md', '<')
  const router = useRouter()
  const searchParams = useSearchParams()

  const [activeView, setActiveView] = useState('month')
  const [selectedEvent, setSelectedEvent] = useState<{ events: FormattedEvent[]; artistDetail: ArtistProps } | null>(
    null
  )
  const [activeShowId, setActiveShowId] = useState<string | null>(null)

  const classNames = cx(styles['calendar'], styles[`calendar--month-view`])
  const getArtistIds = useCallback(() => collectArtistIds(artists), [artists])

  const { loading, filteredItems, selectedMonth, setSelectedDate, activeMonthList } = useCalendar({
    initialDate: dayjs(),
    artistIds: getArtistIds(),
    isTablet: isBelowMd
  })

  const [eventsByDate, setEventsByDate] = useState<EventsByDate>({})
  const eventFormatter = new EventFormatter()

  useEffect(() => {
    if (filteredItems) {
      const { eventsByDate } = eventFormatter.processEventEngineResponse(filteredItems)
      setEventsByDate(eventsByDate)
    }
  }, [filteredItems])

  // Hydrate state from URL
  useEffect(() => {
    const id = searchParams.get('id')
    const startDate = searchParams.get('startdate')
    if (!id || !startDate || Object.keys(eventsByDate)?.length === 0) return

    const dayKey = dayjs(startDate).format(DATE_FORMAT.SHORT_DATE_FORMAT)
    const matchingEvent = eventsByDate[dayKey]?.find(event => event?.id === id)
    const artistDetail = artists.find(artist => artist?.artistId === matchingEvent?.artistId) as ArtistProps

    if (matchingEvent) {
      setSelectedEvent({
        events: eventsByDate[dayKey],
        artistDetail
      })
      setActiveShowId(id)
    }
  }, [eventsByDate, searchParams, artists])

  useEffect(() => {
    disableBodyScroll(Boolean(selectedEvent))
    return () => disableBodyScroll(false)
  }, [selectedEvent])

  // Push to URL
  const updateQueryString = (event: FormattedEvent) => {
    router.push(
      {
        pathname: SLUG_SHOWS,
        query: {
          startdate: dayjs(event.dateTime).format('MM-DD-YYYY'),
          id: event.id
        }
      },
      undefined,
      { shallow: true }
    )
  }

  const handleDateSelection = (events: FormattedEvent[], artistDetail: ArtistProps) => {
    const event = events.find(e => e.artistId === artistDetail.artistId) as FormattedEvent
    setSelectedEvent({ events, artistDetail })
    setActiveShowId(event.id)
    updateQueryString(event)
  }

  const handleTimeSelection = (id: string) => {
    if (!selectedEvent) return
    const event = selectedEvent.events.find(e => e.id === id)
    if (!event) return
    setActiveShowId(id)
    updateQueryString(event)
  }

  const closeSelectedEventDetails = () => {
    setSelectedEvent(null)
    setActiveShowId(null)
    router.push(SLUG_SHOWS, undefined, { shallow: true })
  }

  const renderToggleView = useCallback(
    () => (
      <ViewButtonGroup
        options={['grid', 'month']}
        activeView={activeView}
        setActiveView={setActiveView}
        setHasViewChanged={() => {}}
      />
    ),
    [activeView, setActiveView]
  )

  const renderCalendarView = () => {
    if (activeView === 'grid') {
      return (
        <>
          {renderToggleView()}
          <GridView
            showsCards={showsGridView.showsCards}
            experienceCards={showsGridView.experienceCards}
            sectionHeadline={showsGridView.sectionHeadline}
          />
        </>
      )
    }

    return (
      <CalendarView
        toggleView={renderToggleView()}
        setSelectedDate={setSelectedDate}
        selectedMonth={selectedMonth}
        eventsByDate={eventsByDate}
        activeMonthList={activeMonthList}
        handleDateSelection={handleDateSelection}
        artists={artists}
      />
    )
  }

  return (
    <>
      <div className={styles['hero-container']}>
        {heading && (
          <Heading level={1} justify='center'>
            {heading}
          </Heading>
        )}
        <div className={classNames}>{loading ? <Loading /> : renderCalendarView()}</div>
        <BackGroundImage image={backgroundImage} />
      </div>

      <EventDetails
        isOpen={Boolean(selectedEvent)}
        disclaimer={eventDetailsDisclaimer ?? { html: '' }}
        eventInfo={selectedEvent}
        activeShowId={activeShowId}
        onClose={closeSelectedEventDetails}
        onTimeSelect={handleTimeSelection}
        groupTicketsOfferText={groupTicketsOfferText ?? { html: '' }}
        premiumSuitePageLink={premiumSuitePageLink}
      />
    </>
  )
}

export default Calendar
